import { useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react';
import {
  MAX_STEP_LEVEL,
  type PolicySpec,
  type StepConfig,
  type WorkflowConfig,
} from '@reef/schema';
import {
  ConnectionsRequiredError,
  StaleEditorError,
  api,
  connectionName,
  type ConnectionSummary,
  type MissingConnection,
  type WorkflowSummary,
} from '../api.ts';
import { Icon } from '../components/Icon.tsx';
import { PaneResizer, usePaneWidth } from '../components/PaneResizer.tsx';
import { StepTree } from '../components/StepTree.tsx';
import { SharingEditor } from '../components/SharingEditor.tsx';
import { StepInspector } from '../components/StepInspector.tsx';
import { TriggerEditor } from '../components/TriggerEditor.tsx';

/**
 * The visual builder.
 *
 * Editing is local until you save; saving writes the document, and the response
 * carries the new revision so the next save is checked against it. Last write
 * wins by design — but a losing writer is told, rather than silently clobbering
 * someone.
 */
export function BuilderView({
  workflows,
  workflowId,
  connections,
  policies,
  skills,
  onOpenWorkflow,
  onWorkflowsChanged,
}: {
  workflows: WorkflowSummary[];
  /**
   * From the path, which is `/workflows/:id` — the link the builder agent and
   * `create_workflow` hand back when they have saved one. Null when the
   * builder was reached from the rail, and then the first workflow stands in.
   */
  workflowId: string | null;
  connections: ConnectionSummary[];
  policies: PolicySpec[];
  skills: string[];
  onOpenWorkflow(id: string): void;
  onWorkflowsChanged(): void;
}) {
  /*
   * Which workflow is open lives in the URL rather than here — one copy, so it
   * cannot disagree with the address somebody is about to send on. The fallback
   * is derived rather than written back, because writing a default from an
   * effect races the click that chose something else.
   */
  const selectedWorkflowId = workflowId ?? workflows[0]?.id ?? null;
  const [workflow, setWorkflow] = useState<WorkflowConfig | null>(null);
  const [revision, setRevision] = useState<number | null>(null);
  const [level, setLevel] = useState<string>('read');
  const [selectedStepId, setSelectedStepId] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [notice, setNotice] = useState<{ tone: 'warn' | 'danger'; title: string; body: string } | null>(
    null,
  );
  const [missing, setMissing] = useState<MissingConnection[]>([]);
  const [busy, setBusy] = useState(false);
  const [sidebarWidth, setSidebarWidth] = usePaneWidth('reef.builder.sidebar', 232);
  const [inspectorWidth, setInspectorWidth] = usePaneWidth('reef.builder.inspector', 400);
  /*
   * On a laptop the workflow list is a drawer over the tree rather than a
   * third column beside it — three panes in 900px left the tree 200px wide.
   * The stylesheet decides when; this only says whether it is open, and it
   * closes when a workflow is picked, since the pick is what it was open for.
   */
  const [listOpen, setListOpen] = useState(false);

  /**
   * Open a workflow, unless the reader has moved on.
   *
   * `stillWanted` is the guard every fetch here needs and this one did without
   * for as long as nothing loaded on its own: two documents requested a moment
   * apart do not have to arrive in that order, so clicking the second workflow
   * while the first is in flight can leave the slower first response painted
   * over it — the sidebar highlighting one workflow and the editor showing
   * another, with a Save button wired to whichever won.
   */
  const load = useCallback(async (id: string, stillWanted: () => boolean = () => true) => {
    const result = await api.getWorkflow(id);
    if (!stillWanted()) return;
    setWorkflow(result.workflow);
    setRevision(result.revision);
    setLevel(result.yourLevel);
    setSelectedStepId(result.workflow.steps[0]?.id ?? null);
    setDirty(false);
    setMissing([]);
    /*
     * The notice is deliberately left alone. Clearing it here erased whatever
     * the reader had just been told the moment any load finished — and the load
     * that finishes last is the one nobody asked for, the first workflow this
     * screen opens by default. "This workflow moved on while you were editing"
     * would appear and then vanish on its own, which reads as reef having
     * changed its mind. A notice is cleared by the click that moves on from it.
     */
  }, []);

  useEffect(() => {
    if (!selectedWorkflowId) return;
    let current = true;
    void load(selectedWorkflowId, () => current).catch(
      (error) => current && setNotice(toNotice(error)),
    );
    return () => {
      current = false;
    };
  }, [selectedWorkflowId, load]);

  const editable = level === 'edit' || level === 'admin';
  const selectedStep = useMemo(
    () => (workflow && selectedStepId ? findStep(workflow.steps, selectedStepId) : null),
    [workflow, selectedStepId],
  );

  const mutate = (next: WorkflowConfig) => {
    setWorkflow(next);
    setDirty(true);
  };

  const save = async () => {
    if (!workflow) return;
    setBusy(true);
    try {
      const result = await api.saveWorkflow(workflow.id, workflow, revision, 'edited in the builder');
      setWorkflow(result.workflow);
      setRevision(result.revision);
      setDirty(false);
      setNotice(null);
      onWorkflowsChanged();
    } catch (error) {
      setNotice(toNotice(error));
    } finally {
      setBusy(false);
    }
  };

  const run = async () => {
    if (!workflow) return;
    setBusy(true);
    setMissing([]);
    try {
      const result = await api.runWorkflow(workflow.id);
      setNotice({
        tone: 'warn',
        title: `Run ${result.runId} started`,
        body: `Walking ${result.stepIds.length} root step${result.stepIds.length === 1 ? '' : 's'}. Open the Runs tab to watch it.`,
      });
    } catch (error) {
      if (error instanceof ConnectionsRequiredError) setMissing(error.missing);
      else setNotice(toNotice(error));
    } finally {
      setBusy(false);
    }
  };

  /**
   * A new workflow is created the same way an edit is saved — `PUT` on an id
   * that does not exist yet. It arrives with one empty step rather than none,
   * because a workflow with no steps has nothing to select and the builder
   * opens onto a blank screen.
   */
  const create = async () => {
    setBusy(true);
    const id = `workflow-${Math.random().toString(36).slice(2, 7)}`;
    try {
      await api.saveWorkflow(
        id,
        {
          id,
          name: 'New workflow',
          ownerId: '',
          sharing: [],
          runtime: 'isolate',
          networkAllowlist: [],
          mcpConnections: [],
          skills: [],
          triggers: [],
          steps: [{ id: 'step-1', name: 'First step', parentId: null, level: 1, prompt: '' }],
          revision: 0,
        },
        null,
        'created in the builder',
      );
      onWorkflowsChanged();
      onOpenWorkflow(id);
    } catch (error) {
      setNotice(toNotice(error));
    } finally {
      setBusy(false);
    }
  };

  const duplicate = async () => {
    if (!workflow) return;
    const result = await api.duplicateWorkflow(workflow.id);
    onWorkflowsChanged();
    onOpenWorkflow(result.workflow.id);
  };

  /**
   * Delete the open workflow.
   *
   * Gated on `admin` rather than `edit` — the API refuses anything less, and a
   * button that is only refused after the click is a worse answer than one that
   * is not there. The confirm asks for nothing typed because what it takes away
   * is bounded: the runs, the artifacts and the audit trail all survive, and
   * the audit row saying who removed it outlives the workflow itself.
   */
  const remove = async () => {
    if (!workflow) return;
    if (!globalThis.confirm(`Delete "${workflow.name}"? Its runs and audit history are kept.`)) return;
    setBusy(true);
    try {
      const removed = workflow.id;
      await api.deleteWorkflow(removed);
      onWorkflowsChanged();
      const next = workflows.find((entry) => entry.id !== removed);
      if (next) onOpenWorkflow(next.id);
      else {
        setWorkflow(null);
        setDirty(false);
      }
      setNotice({
        tone: 'warn',
        title: `Deleted ${removed}`,
        body: 'The document is gone. Its runs and audit trail are still there.',
      });
    } catch (error) {
      setNotice(toNotice(error));
    } finally {
      setBusy(false);
    }
  };

  const showInspector = Boolean(workflow && selectedStep);

  return (
    <div
      className={`body resizable builder ${showInspector ? 'with-inspector' : ''}`}
      data-list-open={listOpen}
      style={
        {
          '--sidebar-w': `${sidebarWidth}px`,
          '--inspector-w': `${inspectorWidth}px`,
        } as CSSProperties
      }
    >
      <aside className="sidebar" id="builder-workflows">
        <div className="row">
          <p className="section-title" style={{ flex: 1, margin: 0 }}>
            Workflows
          </p>
          <button
            className="ghost"
            title="New workflow"
            aria-label="New workflow"
            disabled={busy}
            onClick={() => void create()}
          >
            +
          </button>
        </div>
        {workflows.length === 0 && <p className="hint">Nothing shared with you yet.</p>}
        {workflows.map((entry) => (
          <button
            key={entry.id}
            className="list-item"
            aria-current={entry.id === selectedWorkflowId}
            onClick={() => {
              setNotice(null);
              setListOpen(false);
              onOpenWorkflow(entry.id);
            }}
          >
            {entry.name}
            <small>{entry.id}</small>
          </button>
        ))}
      </aside>

      <PaneResizer
        label="Workflow list width"
        edge="left"
        width={sidebarWidth}
        min={160}
        max={480}
        onChange={setSidebarWidth}
      />

      <main className="main">
        {/* Shown only where the list is a drawer; the stylesheet hides it beside a visible list. */}
        <button
          className="ghost builder-list-toggle"
          aria-label="Workflow list"
          aria-expanded={listOpen}
          aria-controls="builder-workflows"
          onClick={() => setListOpen((open) => !open)}
        >
          <Icon name="menu" /> Workflows
        </button>
        {notice && (
          <div className={`banner ${notice.tone}`}>
            <h3>{notice.title}</h3>
            <p style={{ margin: 0 }}>{notice.body}</p>
          </div>
        )}

        {!workflow && <div className="empty">Pick a workflow to open the builder.</div>}

        {workflow && (
          <>
            <div className="row" style={{ marginBottom: '1rem' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <h1 style={{ margin: 0, fontSize: '1.1rem' }}>{workflow.name}</h1>
                <p className="hint" style={{ margin: 0 }}>
                  <code>{workflow.id}</code> · revision {revision ?? workflow.revision} · you have{' '}
                  <strong>{level}</strong>
                </p>
              </div>
              <button onClick={() => void duplicate()}>Duplicate</button>
              {level === 'admin' && (
                <button className="danger" disabled={busy} onClick={() => void remove()}>
                  Delete
                </button>
              )}
              <button onClick={() => void run()} disabled={busy}>
                Run
              </button>
              <button className="primary" onClick={() => void save()} disabled={!editable || !dirty || busy}>
                {dirty ? 'Save changes' : 'Saved'}
              </button>
            </div>

            {/*
              Two kinds of blocker, split because the remedy differs: a missing
              OAuth link is yours to fix with a button, a missing workspace
              credential is an admin's. Offering a Connect button for the second
              would send someone into a flow that cannot help them.
            */}
            {missing.some((entry) => entry.connectUrl) && (
              <div className="banner warn">
                <h3>You haven't authorized every connection this workflow uses</h3>
                <p style={{ marginTop: 0 }}>
                  Runs execute with your own access, so these have to be connected under your
                  account before this workflow can run for you.
                </p>
                <div className="row wrap">
                  {missing
                    .filter((entry) => entry.connectUrl)
                    .map((entry) => (
                      <a className="button primary" key={entry.connectionId} href={entry.connectUrl!}>
                        Connect {entry.connectionName}
                      </a>
                    ))}
                </div>
              </div>
            )}

            {missing.some((entry) => !entry.connectUrl) && (
              <div className="banner danger">
                <h3>This workspace is missing a credential</h3>
                <p style={{ margin: 0 }}>
                  {missing
                    .filter((entry) => !entry.connectUrl)
                    .map((entry) => entry.message)
                    .join(' ')}
                </p>
              </div>
            )}

            <div className="card">
              <h2>Defaults inherited by every step</h2>
              <div className="row wrap">
                <span className="pill">{workflow.runtime}</span>
                {typeof workflow.policy === 'string' && (
                  <span className="pill accent">{workflow.policy}</span>
                )}
                {/*
                  The connection's own name, not the mount identifier. `ch →
                  clickhouse-prod` was two spellings of the same thing and
                  neither was the name anybody uses for it; the mount name still
                  matters to whoever writes the prompt, so it stays in the title.
                */}
                {workflow.mcpConnections.map((connection) => (
                  <span
                    key={connection.as}
                    className="pill"
                    title={`Mounted as ${connection.as}`}
                  >
                    {connectionName(connections, connection.id)}
                  </span>
                ))}
                {workflow.networkAllowlist.length === 0 ? (
                  <span className="pill">network: none</span>
                ) : (
                  /*
                    A pill holding a list rather than a word, so it truncates.
                    Four hosts is 450px of text in a 315px card, and a pill has
                    `nowrap` — un-truncated it runs out past the card's border
                    and over whatever is beside it. The full list stays in the
                    `title`, which is where a summary's detail belongs.
                  */
                  <span className="pill summary" title={workflow.networkAllowlist.join(', ')}>
                    <span className="pill-text">{workflow.networkAllowlist.join(', ')}</span>
                  </span>
                )}
              </div>
              {workflow.triggers.length > 0 && (
                <div className="row wrap" style={{ marginTop: '0.5rem' }}>
                  {workflow.triggers.map((trigger, index) => (
                    // Truncated for the same reason as the allowlist above: a
                    // trigger subscribed to four events describes itself in a
                    // sentence, and a pill is not a paragraph.
                    <span key={index} className="pill accent summary" title={describeTrigger(trigger)}>
                      <span className="pill-text">{describeTrigger(trigger)}</span>
                    </span>
                  ))}
                </div>
              )}
            </div>

            <TriggerEditor
              workflow={workflow}
              editable={editable}
              onChange={(triggers) => mutate({ ...workflow, triggers })}
            />

            {/*
              Beside the triggers, because they answer the same shape of
              question — what reaches this workflow, and who. A workflow with no
              grants is private, and until now the studio offered no way to
              change that at all: people shared a *link* to a run instead, which
              is a bearer token that bypasses the session entirely.

              Keyed by id so switching workflows refetches rather than showing
              the previous one's grants while the new ones load — a stale
              audience is the one wrong answer this card must never give.
            */}
            {workflow.id && <SharingEditor key={workflow.id} workflowId={workflow.id} />}

            <p className="section-title">
              Steps · max {MAX_STEP_LEVEL} levels deep
            </p>
            <StepTree
              workflow={workflow}
              connections={connections}
              selectedId={selectedStepId}
              editable={editable}
              onSelect={setSelectedStepId}
              onAddChild={(parentId) => mutate(addChild(workflow, parentId))}
              onAddSibling={(afterId) => mutate(addSibling(workflow, afterId))}
              onDelete={(stepId) => {
                mutate(deleteStep(workflow, stepId));
                if (selectedStepId === stepId) setSelectedStepId(null);
              }}
            />
          </>
        )}
      </main>

      {showInspector && (
        <PaneResizer
          label="Step inspector width"
          edge="right"
          width={inspectorWidth}
          min={280}
          max={720}
          onChange={setInspectorWidth}
        />
      )}

      {workflow && selectedStep && (
        <aside className="inspector">
          <StepInspector
            step={selectedStep}
            workflow={workflow}
            connections={connections}
            policies={policies}
            skills={skills}
            editable={editable}
            dirty={dirty}
            onChange={(next) => mutate(replaceStep(workflow, next))}
            onCommit={() => void save()}
          />
        </aside>
      )}
    </div>
  );
}

// ------------------------------------------------------------ tree mutation

function findStep(steps: readonly StepConfig[], id: string): StepConfig | null {
  for (const step of steps) {
    if (step.id === id) return step;
    const found = step.childSteps ? findStep(step.childSteps, id) : null;
    if (found) return found;
  }
  return null;
}

function mapSteps(steps: readonly StepConfig[], fn: (step: StepConfig) => StepConfig): StepConfig[] {
  return steps.map((step) => {
    const mapped = fn(step);
    return mapped.childSteps ? { ...mapped, childSteps: mapSteps(mapped.childSteps, fn) } : mapped;
  });
}

function replaceStep(workflow: WorkflowConfig, next: StepConfig): WorkflowConfig {
  return {
    ...workflow,
    steps: mapSteps(workflow.steps, (step) => (step.id === next.id ? { ...next } : step)),
  };
}

function newStep(parent: StepConfig | null): StepConfig {
  const suffix = Math.random().toString(36).slice(2, 7);
  return {
    id: parent ? `${parent.id}.${suffix}` : `step-${suffix}`,
    name: 'New step',
    parentId: parent?.id ?? null,
    level: (parent?.level ?? 0) + 1,
    prompt: '',
  };
}

function addChild(workflow: WorkflowConfig, parentId: string): WorkflowConfig {
  return {
    ...workflow,
    steps: mapSteps(workflow.steps, (step) => {
      if (step.id !== parentId || step.level >= MAX_STEP_LEVEL) return step;
      return { ...step, childSteps: [...(step.childSteps ?? []), newStep(step)] };
    }),
  };
}

/**
 * Insert a sibling after the given step, at the same level and under the same
 * parent. `null` appends a new root step.
 */
function addSibling(workflow: WorkflowConfig, afterStepId: string | null): WorkflowConfig {
  if (afterStepId === null) {
    return { ...workflow, steps: [...workflow.steps, newStep(null)] };
  }

  const insertInto = (steps: readonly StepConfig[], parent: StepConfig | null): StepConfig[] => {
    const index = steps.findIndex((step) => step.id === afterStepId);
    if (index !== -1) {
      const copy = [...steps];
      copy.splice(index + 1, 0, newStep(parent));
      return copy;
    }
    return steps.map((step) =>
      step.childSteps ? { ...step, childSteps: insertInto(step.childSteps, step) } : step,
    );
  };

  return { ...workflow, steps: insertInto(workflow.steps, null) };
}

function deleteStep(workflow: WorkflowConfig, stepId: string): WorkflowConfig {
  const prune = (steps: readonly StepConfig[]): StepConfig[] =>
    steps
      .filter((step) => step.id !== stepId)
      .map((step) => (step.childSteps ? { ...step, childSteps: prune(step.childSteps) } : step));

  return { ...workflow, steps: prune(workflow.steps) };
}

// ------------------------------------------------------------------ display

function describeTrigger(trigger: WorkflowConfig['triggers'][number]): string {
  // Conditions are counted rather than spelled out: the summary line is for
  // recognising a trigger at a glance, and the editor below shows the detail.
  const filtered = 'match' in trigger && trigger.match?.length ? ` +${trigger.match.length} filter` : '';

  switch (trigger.type) {
    case 'manual':
      return trigger.triggerFromEveryone ? 'manual · anyone' : 'manual';
    case 'schedule':
      return `schedule ${trigger.cron ?? `${(trigger.delayMs ?? 0) / 1000}s`}`;
    case 'webhook':
      return `webhook ${trigger.events?.length ? trigger.events.join(', ') : 'any event'}${filtered}`;
    case 'linear':
      return `linear ${trigger.events.join(', ')}${trigger.state ? ` → ${trigger.state}` : ''}${filtered}`;
    case 'github':
      return `github ${trigger.events.join(', ')}${filtered}`;
    case 'jira':
      return `jira ${trigger.events.join(', ')}${trigger.status ? ` → ${trigger.status}` : ''}${filtered}`;
    case 'slack':
      return `slack ${trigger.events.join(', ')}${filtered}`;
  }
}

function toNotice(error: unknown): { tone: 'warn' | 'danger'; title: string; body: string } {
  if (error instanceof StaleEditorError) {
    return {
      tone: 'warn',
      title: 'Someone else saved first',
      body: 'This workflow changed in the repository while you were editing. Reload to pick up their version, then reapply your change.',
    };
  }
  return {
    tone: 'danger',
    title: 'That did not work',
    body: error instanceof Error ? error.message : String(error),
  };
}
